import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Sprout, Handshake } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import WordFadeIn from "@/components/WordFadeIn";
import DrawIcon from "@/components/DrawIcon";
import heroRegional from "@/assets/hero-regional.jpg";
import Seo from "@/components/Seo";

const partners = [
  {
    name: "Haus Neersdonk",
    type: "Freilandeier aus mobiler Stallhaltung",
    location: "Tönisvorst",
    url: "https://www.haus-neersdonk.de/h%C3%BChnereier",
  },
];

const benefits = [
  { icon: Sprout, title: "Kurze Transportwege", desc: "Kurze Wege für frische Ware und weniger Belastung für die Umwelt." },
  { icon: Handshake, title: "Region unterstützen", desc: "Wir stärken regionale Strukturen und pflegen faire Partnerschaften." },
  { icon: MapPin, title: "Natur- und Tierschutz", desc: "Ökologische Nachhaltigkeit sowie Aspekte von Natur- und Tierschutz prägen unser Sortiment." },
];

const Regionales = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <>
      <Seo title="Regionale Produkte | Rewe Kotlarski Meerbusch-Osterath" description="Regionale Partner wie Haus Neersdonk und die Bäckerei Wiehler: kurze Wege, frische Ware und Produkte aus der Region bei Rewe Kotlarski in Meerbusch." />
      <PageHeader
        tag="Regionales"
        title="Regionalität mit Haltung"
        subtitle="Rewe Kotlarski setzt auf kurze Transportwege, ökologische Nachhaltigkeit, Naturschutz, Tierschutz und die Unterstützung der Region."
        heroImage={heroRegional}
        drawIconVariant="leaf"
      />

      {/* Benefits */}
      <section className="relative py-20 px-6 md:px-16 bg-secondary/30 overflow-hidden">
        <DrawIcon
          variant="leaf"
          size={160}
          delay={0.5}
          className="absolute right-8 md:right-16 top-8 opacity-15 hidden md:block"
        />
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
          {benefits.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 * i }}
              className="bg-card border border-border p-8 flex flex-col items-center text-center md:items-start md:text-left h-full"
            >
              <b.icon className="w-8 h-8 text-primary-bright mb-5 shrink-0" aria-hidden="true" />
              <h2 className="font-display text-xl font-semibold text-foreground mb-2" style={{ textWrap: "pretty" as any }}>{b.title}</h2>
              <p className="text-muted-foreground font-body leading-relaxed" style={{ textWrap: "pretty" as any }}>{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Partners */}
      <section ref={ref} className="py-20 px-6 md:px-16">
        <div className="max-w-6xl mx-auto">
          <WordFadeIn
            text="Premium-Partner"
            as="h2"
            className="font-display text-3xl md:text-5xl font-bold text-foreground text-center md:text-left mb-16 [hyphens:none]"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {partners.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                className="border border-border p-6 hover:bg-secondary/50 transition-colors duration-300 h-full"
              >
                <h3 className="font-display text-lg font-semibold text-foreground" style={{ textWrap: "pretty" as any }}>{p.name}</h3>
                <p className="text-primary-bright font-body text-sm mt-1">{p.type}</p>
                <p className="text-muted-foreground font-body text-sm mt-2 flex items-start gap-2">
                  <MapPin size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{p.location}</span>
                </p>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-primary-bright font-display text-sm tracking-wide focus:outline-none focus:ring-2 focus:ring-ring rounded"
                  >
                    Website besuchen
                    <span aria-hidden="true">→</span>
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Regionales;
